import type { ListerCardPrice, ListerCardRead, ListerCertEnvelope, ListerExisting } from "../../../lib/api";
import { fmtCents } from "../../../lib/format";
import { CARD_CONDITIONS, conditionChoices, type ListerCardCondition } from "../../../lib/card-condition";
import { AUTOGRAPH_TYPES, autographChoices, type ListerAutographType } from "../../../lib/card-autograph";

const FLAG_TEXT: Record<string, string> = {
  no_price_proposed: "No price was proposed — set one before creating the product.",
  fewer_than_3_in_grade_sold: "Fewer than three in-grade sales were found, so the median was not used.",
  asking_price_not_a_sale: "This is an asking price, not a sale.",
  pricecharting_disagrees_2x: "PriceCharting disagrees by more than 2× — check the card before posting.",
  premium_tier_no_reference: "PriceCharting has no column for this premium tier, so there is no reference price.",
  raised_to_cost_floor: "Raised to the cost × 1.25 floor.",
  no_grade_read: "No grader and grade could be read from the photographs.",
  // RAW ladder (Plan 3). "no_condition_chosen" is the OPERATOR's own missing input, never a
  // suggestion — the sentence says so plainly and never implies the system picked one.
  no_condition_chosen: "No condition has been stated — pick one and the price follows.",
  fewer_than_3_in_condition: "Fewer than three ungraded comparables in this condition, so no price was computed.",
};

/** In the operator's own words, where a proposed price came from — sold comps in grade (or,
 *  for a raw card, in the operator's stated condition), the marketplace's own guidance, or an
 *  asking price that was never a sale. */
const BASIS_LABEL: Record<ListerCardPrice["basis"], string> = {
  sold_in_grade: "sold comparables in grade",
  ebay_guidance: "eBay's own price guidance",
  active_in_grade: "an asking price, not a sale",
  sold_in_condition: "sold comparables in this condition",
  active_in_condition: "asking prices in this condition, not sales",
  none: "no trustworthy basis",
};

const refSourceLabel = (s: string | null): string | null =>
  s === "pricecharting" ? "PriceCharting" : s === "pricecharting_ungraded" ? "PriceCharting (ungraded)" : s;

/** Where the vision read and the grading company's record disagree. This FORMATS the list the
 *  server already computed (CertEnvelope.conflicts, from reconcileCertAndRead — spec §8); it does
 *  not recompute it. A browser-side normaliser would eventually disagree with the server about
 *  what counts as a conflict, and the operator would see a different story from the one the
 *  proposal was built on. The grader wins on every entry; this exists so the operator can see WHY
 *  the proposal says what it says.
 *
 *  Gated on `cert_bound` as well as `cert_verified`: a record that cannot be tied to the
 *  certificate that was asked about (ARS — its page never prints the cert number back) may
 *  corroborate the photographs, but it is never a verification of THIS card, so its fields must
 *  never be shown as "the grader's record wins" — that would tell the operator to trust a value
 *  the grading company never actually confirmed as theirs. `field_source` is checked per field on
 *  top of that as a second, cheap line of defence: a conflict only exists for a field the record
 *  actually filled in, so this should never trim anything reconcileCertAndRead didn't already
 *  guarantee — but the whole point of this panel is not to assume that from one flag alone. */
export function readVsGraderConflicts(cert: ListerCertEnvelope | null): Array<{ field: string; read: string; grader: string }> {
  if (!cert || !cert.cert_verified || !cert.cert_bound) return [];
  return cert.conflicts
    .filter((c) => c.read !== null && c.cert !== null && cert.field_source[c.field] === "cert")
    .map((c) => ({ field: c.field, read: String(c.read), grader: String(c.cert) }));
}

export default function CardPanel({ read, cert, price, existing, dup_check, condition, onCondition, autograph_type, onAutographType, saving, dup_candidates }: {
  read: ListerCardRead; cert: ListerCertEnvelope | null; price: ListerCardPrice | null;
  existing: ListerExisting | null; dup_check: "deferred" | "ok" | "failed" | "warned" | "title_searched";
  /** The operator's own choice for a RAW card, or null until they make one — never defaulted. */
  condition: ListerCardCondition | null;
  onCondition: (condition: ListerCardCondition) => void;
  /** Where the autograph sits, stated by the operator — or null until they answer. Present for
   *  BOTH a raw and a graded card the read flagged; never defaulted. */
  autograph_type: ListerAutographType | null;
  onAutographType: (t: ListerAutographType) => void;
  /** Disables the select — while the page is read-only (posted/mid-pipeline) or while the
   *  edit just made is still being saved and repriced. */
  saving: boolean;
  /** Possible duplicates a RAW card's title search found (never the restock path's `existing`). */
  dup_candidates: ListerExisting[];
}) {
  // `facts` is the reconciled identity — precedence was decided server-side. `read` is only the
  // fallback for a session whose cert stage has not run (or a raw card).
  const record = cert?.record ?? null;
  const facts = cert?.facts ?? null;
  // THE RULE this whole panel exists to honour: `cert_verified` only says the lookup returned a
  // record ("status === ok"). It says nothing about whether that record is THIS certificate's
  // (`cert_bound`) or whether any specific displayed value actually came from it
  // (`field_source`) — badging on `cert_verified` alone would claim things the grading company
  // never said. `bound` gates the whole "verified" claim; `field_source` gates one field at a
  // time (used below for the grade line specifically, and inside readVsGraderConflicts).
  const bound = cert?.cert_bound === true;
  const boundVerified = cert?.cert_verified === true && bound;
  const grader = facts?.grader ?? read.grader;
  const grade = facts?.grade ?? read.grade;
  const certNo = facts?.cert ?? read.cert;
  const isSlab = facts?.is_slab ?? read.is_slab;
  const conflicts = readVsGraderConflicts(cert);
  const label = [grader, grade].filter(Boolean).join(" ");
  // A record can verify and bind and still not have supplied a particular field (one grader
  // routinely returns "ok" with no grade at all when its population table can't tell which
  // bucket is this cert's) — field_source is the only thing that says so, per field.
  const gradeConfirmed = boundVerified && cert?.field_source.grade === "cert";

  return (
    <section className="bg-zinc-900 border border-zinc-800 rounded-md p-3 space-y-2">
      <h2 className="text-zinc-400 text-xs uppercase tracking-wide">Card</h2>
      {isSlab ? (
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="text-zinc-100 text-base">{label || "grade unknown"}</span>
          {certNo && <span className="text-zinc-300 text-xs">cert {certNo}</span>}
          {boundVerified && record?.population && <span className="text-zinc-500 text-xs">pop {record.population}</span>}
        </div>
      ) : (
        <label className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-zinc-300">Condition</span>
          <select
            aria-label="Card condition"
            value={condition ?? ""}
            disabled={saving}
            onChange={(e) => {
              const v = e.target.value;
              // The empty placeholder option is not a condition — never forward it as one.
              if ((CARD_CONDITIONS as readonly string[]).includes(v)) onCondition(v as ListerCardCondition);
            }}
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-100 disabled:opacity-50"
          >
            {/* The model never proposes a condition — an unset one reads as unset, never as
                a default Near Mint. */}
            <option value="">— pick a condition —</option>
            {conditionChoices(facts?.category ?? read.category).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </label>
      )}

      {/* Appears for EITHER a raw or a graded card the read flagged — a slabbed sticker
          autograph is still a sticker autograph. Never a default: the operator answers, or the
          server's posting gate (src/lister/validate.ts) refuses Create. */}
      {(facts?.autograph ?? read.autograph) && (
        <label className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-zinc-300">Autograph</span>
          <select
            aria-label="Autograph"
            value={autograph_type ?? ""}
            disabled={saving}
            onChange={(e) => {
              const v = e.target.value;
              // The empty placeholder is not an answer — never forward it as one.
              if ((AUTOGRAPH_TYPES as readonly string[]).includes(v)) onAutographType(v as ListerAutographType);
            }}
            // min-w-0 lets the flex item shrink below its content's natural width; max-w-full
            // and truncate cap the closed box at the panel's own width instead of the widest
            // option's ("No autograph — the photographs were misread") — a phone at 390px has
            // no room for that phrase in one line, and the review page's own browser test
            // measures exactly this kind of horizontal overflow.
            className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-100 disabled:opacity-50 min-w-0 max-w-full truncate"
          >
            <option value="">— on the card, or on a sticker? —</option>
            {autographChoices().map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </label>
      )}

      {/* Four distinct, ordinary outcomes — never an error state. Two of the eight graders are
          unreachable today (one behind a bot check, one because the company's site is down), so
          landing anywhere but the last branch is routine, not exceptional. */}
      {isSlab && (
        boundVerified ? (
          facts?.source_of_record === "cert" ? (
            <p className="text-xs text-green-300" role="status">
              Cert verified with {record!.grader} ({record!.source === "api" ? "official API" : "cert lookup"}).
              {grade && !gradeConfirmed && " The grade shown is from the photographs — the record didn't confirm it."}
            </p>
          ) : (
            <p className="text-xs text-yellow-200" role="status">
              Cert verified with {record!.grader}, but its record confirmed nothing about this specific card — everything below rests on the photographs.
            </p>
          )
        ) : cert?.cert_verified ? (
          <p className="text-xs text-yellow-200" role="status">
            {record!.grader}'s record was found, but it cannot be tied to certification #{record!.cert} — it corroborates the photographs at best; everything below still rests on them.
          </p>
        ) : (
          <p className="text-xs text-yellow-200" role="status">
            Cert not verified{record ? ` (${record.status})` : " (no cert lookup)"} — everything below rests on the photographs alone.
          </p>
        )
      )}

      {/* The duplicate answer from the end of the CERT stage (spec §11). Review.tsx only shows
          session.existing once a session is restocked, so without this it would be invisible. */}
      {existing ? (
        <div className="text-xs text-amber-200 space-y-1" role="alert">
          <p>Already in Shopify: <span className="text-zinc-100">{existing.title}</span> — this certification number is already on a product. Restock it instead of creating a second one.</p>
          <a href={existing.admin_url} target="_blank" rel="noopener" className="underline text-zinc-300">open in Shopify ↗</a>
        </div>
      ) : dup_check === "failed" ? (
        <p className="text-xs text-amber-200">Duplicate check unavailable — search Shopify for this cert before posting.</p>
      ) : dup_check === "warned" ? (
        // A RAW card has no unique key (no certificate to search by), so this is a warning to
        // look before adding another — never a match, and never a restock affordance.
        <div className="text-xs text-amber-200 space-y-1" role="alert">
          <p>{dup_candidates.length} product{dup_candidates.length === 1 ? "" : "s"} in Shopify share this card's title — an ungraded card has no unique key, so this cannot be checked automatically. Look before adding another.</p>
          <ul className="list-disc pl-5">
            {dup_candidates.map((c) => (
              <li key={c.id}>
                <span className="text-zinc-100">{c.title}</span> — <a href={c.admin_url} target="_blank" rel="noopener" className="underline text-zinc-300">open in Shopify ↗</a>
              </li>
            ))}
          </ul>
        </div>
      ) : dup_check === "title_searched" ? (
        <p className="text-xs text-zinc-500">No product matched this card's title — a title search cannot prove there is no duplicate; check Shopify yourself if unsure.</p>
      ) : null}

      {conflicts.length > 0 && (
        <div className="text-xs text-amber-200 space-y-1">
          <p>The photographs and the grading company's record disagree — the grading company's record wins:</p>
          <ul className="list-disc pl-5">
            {conflicts.map((c) => <li key={c.field}>{c.field}: <span className="text-zinc-100">{c.grader}</span> (photographs read "{c.read}")</li>)}
          </ul>
        </div>
      )}

      {price && (
        <div className="text-xs text-zinc-400 space-y-1">
          <div>
            {price.suggested_cents === null ? (
              <span className="text-yellow-200">No price proposed — set one below.</span>
            ) : (
              <>
                Price basis: {fmtCents(price.suggested_cents)} — {BASIS_LABEL[price.basis]}
                {price.basis === "sold_in_grade" && ` (${price.sold_n} in-grade sales)`}
                {price.basis === "sold_in_condition" && ` (${price.sold_n} ungraded comps)`}
              </>
            )}
            {price.reference_cents !== null && <> · {refSourceLabel(price.reference_source)} reference {fmtCents(price.reference_cents)}</>}
          </div>
          <div className="text-zinc-500">{price.explain}</div>
          {price.flags.length > 0 && (
            <ul className="list-disc pl-5 text-amber-200">
              {price.flags.map((f) => <li key={f}>{FLAG_TEXT[f] ?? f}</li>)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
